// src/components/admin/SystemConfigTab.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  TextField,
  Button,
  Chip,
  Switch,
  IconButton,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";
import { useTranslation } from "../../i18n";
import { createData, fetchData } from "../../api/apiHelpers";
import api from "../../api/api";
import { glass, glassTableSx } from "../../theme/glassTokens";

interface ConfigItem {
  key: string;
  value: string;
  value_type: string;
  description: string;
  updated_at: string | null;
}

type ConfigResponse = Record<string, {
  value: string;
  value_type: string;
  description: string;
  updated_at: string | null;
}>;

const SystemConfigTab: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchData<ConfigResponse>("/billing/admin/system-config/");
      const items: ConfigItem[] = Object.entries(resp).map(([key, val]) => ({
        key,
        value: val.value,
        value_type: val.value_type,
        description: val.description,
        updated_at: val.updated_at,
      }));
      items.sort((a, b) => a.key.localeCompare(b.key));
      setConfigs(items);
      setEdits({});
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleEdit = (key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  };

  const hasChanges = Object.keys(edits).some(
    (key) => edits[key] !== configs.find((c) => c.key === key)?.value
  );

  const handleSave = async () => {
    const updates: Record<string, string> = {};
    for (const [key, value] of Object.entries(edits)) {
      const original = configs.find((c) => c.key === key);
      if (original && original.value !== value) {
        updates[key] = value;
      }
    }
    if (Object.keys(updates).length === 0) return;

    setSaving(true);
    try {
      // Direct api.put needed — endpoint requires PUT (full replacement), not PATCH
      await api.put("/billing/admin/system-config/update/", updates);
      enqueueSnackbar(t("admin.config_saved", "Configuration saved"), { variant: "success" });
      await loadConfig();
    } catch {
      enqueueSnackbar(t("admin.config_save_failed", "Failed to save configuration"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderEditor = (config: ConfigItem) => {
    const currentValue = edits[config.key] ?? config.value;

    if (config.value_type === "bool" || config.value.toLowerCase() === "true" || config.value.toLowerCase() === "false") {
      const boolValue = currentValue.toLowerCase() === "true";
      return (
        <Switch
          size="small"
          checked={boolValue}
          onChange={(e) => handleEdit(config.key, e.target.checked ? "true" : "false")}
        />
      );
    }

    return (
      <TextField
        size="small"
        value={currentValue}
        onChange={(e) => handleEdit(config.key, e.target.value)}
        variant="outlined"
        sx={{ minWidth: 120 }}
      />
    );
  };

  // ── XXMM sync + Trial Registry ── moved to Superadmin → Overview tab.
  // Joan, 2026-05-27: SystemConfig now only owns the runtime configuration
  // table; both data-sync rows live in PlatformOverview via <SyncRows />.

  return (
    <Box>
      {/* XXMM Legacy Sync + Clinical Trial Registry now live in the
          Overview tab (PlatformOverview → <SyncRows />). */}

      {/* ── Runtime Configuration ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: textPrimary }}>
          {t("admin.system_config", "Runtime Configuration")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={t("admin.refresh", "Refresh")}>
            <IconButton size="small" onClick={loadConfig} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small" sx={glassTableSx}>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", color: glass.textSecondary } }}>
                <TableCell>{t("admin.col_key", "Key")}</TableCell>
                <TableCell>{t("admin.col_value", "Value")}</TableCell>
                <TableCell>{t("admin.col_description", "Description")}</TableCell>
                <TableCell>{t("admin.col_last_updated", "Last Updated")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 4 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : configs.map((config) => {
                    const isModified = edits[config.key] !== undefined && edits[config.key] !== config.value;
                    return (
                      <TableRow
                        key={config.key}
                        sx={{
                          "&:last-child td": { border: 0 },
                          background: isModified ? alpha(theme.palette.warning.main, 0.04) : undefined,
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                              {config.key}
                            </Typography>
                            {isModified && <Chip size="small" label={t("admin.modified", "modified")} color="warning" sx={{ fontSize: "0.65rem" }} />}
                          </Box>
                        </TableCell>
                        <TableCell>{renderEditor(config)}</TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", maxWidth: 300 }}>
                          {/* Colour lives on the child: `& tbody td` in
                              glassTableSx outranks this cell's own sx. */}
                          <Box component="span" sx={{ color: glass.textSecondary }}>
                            {config.description}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          {config.updated_at
                            ? new Date(config.updated_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                          
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SystemConfigTab;
